import React, { useState, useEffect } from 'react';
import { Plus, Target } from 'lucide-react';
import GoalTreeNode from '../GoalTreeNode';
import GoalForm from '../GoalForm';
import { API_URL } from '../../config/api.js';


const GoalTracker = ({ user, onLogout }) => {
  const [goals, setGoals] = useState([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [parentGoalId, setParentGoalId] = useState(null);

  useEffect(() => {
    loadGoals();
  }, [user]);

  const loadGoals = async () => {
    try {
        const response = await fetch(`${API_URL}/users/${user._id}/goals/root`);
        const loadedGoals = await response.json();
        setGoals(loadedGoals);
    } catch (error) {
      console.error('Error loading goals:', error);
    }
  };

  const saveGoal = async (goal) => {
    try {
      const goalData = { ...goal, userId: user._id };
      
      if (goal._id) {
        // Update existing goal
        await fetch(`${API_URL}/goals/${goal._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(goalData)
        });
      } else {
        // Create new goal
        await fetch(`${API_URL}/goals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(goalData)
        });
      }
      
      loadGoals(); // Reload goals
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      // Delete the goal and all its subgoals recursively
      const toDelete = [goalId];
      const findSubgoals = (parentId) => {
        goals.forEach(g => {
          if (g.parentId === parentId) {
            toDelete.push(g._id);
            findSubgoals(g._id);
          }
        });
      };
      findSubgoals(goalId);

      // Delete from storage
      for (const id of toDelete) {
        await window.storage.delete(`goal:${id}`);
      }

      setGoals(goals.filter(g => !toDelete.includes(g._id)));
      if (selectedGoal?._id === goalId) setSelectedGoal(null);
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const createGoal = (goalData) => {
    const newGoal = {
      id: Date.now().toString(),
      ...goalData,
      parentId: parentGoalId,
      created: new Date().toISOString(),
      history: [],
      currentPeriodStart: new Date().toISOString(),
    };
    setGoals([...goals, newGoal]);
    saveGoal(newGoal);
    setShowGoalForm(false);
    setParentGoalId(null);
  };

  const updateGoal = (updatedGoal) => {
    const newGoals = goals.map(g => g._id === updatedGoal._id ? updatedGoal : g);
    setGoals(newGoals);
    saveGoal(updatedGoal);
    if (selectedGoal?._id === updatedGoal._id) setSelectedGoal(updatedGoal);
    setEditingGoal(null);
  };

  const logProgress = (goalId, quantity=1) => {
    const goal = goals.find(g => g._id === goalId);
    if (!goal) return;

    const now = new Date();
    const periodStart = new Date(goal.currentPeriodStart);
    const periodEnd = new Date(periodStart);

    switch (goal.timeframe) {
      case 'daily': periodEnd.setDate(periodEnd.getDate() + 1); break;
      case 'weekly': periodEnd.setDate(periodEnd.getDate() + 7); break;
      case 'monthly': periodEnd.setMonth(periodEnd.getMonth() + 1); break;
      case 'annually': periodEnd.setFullYear(periodEnd.getFullYear() + 1); break;
    }

    if (now >= periodEnd) {
      // For decrease goals: achieved = stayed under target (progress < target)
      // For increase goals: achieved = met or exceeded target (progress >= target)
      const achieved = goal.goalDirection === 'decrease' 
        ? goal.progress <= goal.target 
        : goal.progress >= goal.target;
      
      const updatedGoal = applyAutoScaling(goal, achieved);
      
      updatedGoal.history.push({
        periodStart: goal.currentPeriodStart,
        periodEnd: now.toISOString(),
        target: goal.target,
        achieved: goal.progress,
        success: achieved,
        scaledTo: updatedGoal.target,
      });
      updatedGoal.progress = quantity ? quantity : 0;
      updatedGoal.currentPeriodStart = now.toISOString();
      
      updateGoal(updatedGoal);
    } else {
      const updatedGoal = { ...goal, progress: goal.progress + (quantity ? quantity : 0) };
      updateGoal(updatedGoal);
    }
  };

  const applyAutoScaling = (goal, achieved) => {
    const scalePercent = parseFloat(goal.scalePercent) / 100;
    let newTarget = goal.target;
    const isDecreaseGoal = goal.goalDirection === 'decrease';

    // For decrease goals, logic is inverted:
    // - If achieved (stayed under target), make it harder by lowering the target
    // - If not achieved (went over target), make it easier by raising the target
    if (isDecreaseGoal) {
      if (achieved && goal.scaleUpEnabled) {
        // Success on decrease goal = lower the target (make it harder)
        const decrease = goal.target * scalePercent;
        newTarget = goal.roundUp ? Math.ceil(goal.target - decrease) : Math.floor(goal.target - decrease);
      } else if (!achieved && goal.scaleDownEnabled) {
        // Failure on decrease goal = raise the target (make it easier)
        const increase = goal.target * scalePercent;
        newTarget = goal.roundUp ? Math.ceil(goal.target + increase) : Math.floor(goal.target + increase);
      }
    } else {
      // Standard increase goals
      if (achieved && goal.scaleUpEnabled) {
        const increase = goal.target * scalePercent;
        newTarget = goal.roundUp ? Math.ceil(goal.target + increase) : Math.floor(goal.target + increase);
      } else if (!achieved && goal.scaleDownEnabled) {
        const decrease = goal.target * scalePercent;
        newTarget = goal.roundUp ? Math.ceil(goal.target - decrease) : Math.floor(goal.target - decrease);
      }
    }

    newTarget = Math.max(goal.minTarget || 1, Math.min(newTarget, goal.maxTarget || 1000));

    return { ...goal, target: newTarget };
  };

  const manualScale = (goalId, direction) => {
    const goal = goals.find(g => g._id === goalId);
    if (!goal) return;

    const updatedGoal = applyAutoScaling(goal, direction === 'up');
    updatedGoal.history.push({
      periodStart: goal.currentPeriodStart,
      periodEnd: new Date().toISOString(),
      target: goal.target,
      achieved: goal.progress,
      success: direction === 'up',
      scaledTo: updatedGoal.target,
      manual: true,
    });
    updatedGoal.progress = 0;
    updatedGoal.currentPeriodStart = new Date().toISOString();
    updateGoal(updatedGoal);
  };

  // Add logout button to header
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Target className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Goal Tracker</h1>
                <p className="text-sm text-gray-600">Welcome, {user.firstName || user.username}!</p>
              </div>
            </div>
          </div>

           <div className="flex items-center justify-between mb-6">
             <button
               onClick={() => { setParentGoalId(null); setShowGoalForm(true); }}
               className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
             >
               <Plus className="w-5 h-5" />
               New Goal
             </button>
              <div className="flex items-center gap-4">
                <button
                  onClick={onLogout}
                  className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition"
                >
                  Logout
                </button>
              </div>
           </div>

           {goals.length === 0 && !showGoalForm && (
             <div className="text-center py-12">
               <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
               <p className="text-gray-500 text-lg">No goals yet. Create your first goal to get started!</p>
             </div>
           )}

           <div className="space-y-4">
             {goals.map(goal => (
               <GoalTreeNode
                 key={goal._id}
                 goal={goal}
                 allGoals={goals}
                 getSubgoals={() => []}
                 onLog={logProgress}
                 onEdit={(g) => { setEditingGoal(g); setShowGoalForm(true); }}
                 onDelete={deleteGoal}
                 onView={setSelectedGoal}
                 onManualScale={manualScale}
                 onAddSubgoal={() => []}
                 level={0}
               />
             ))}
           </div>
         {/* </div> */}

         {showGoalForm && (
           <GoalForm
             goal={editingGoal}
             parentGoalId={parentGoalId}
             parentGoal={parentGoalId ? goals.find(g => g._id === parentGoalId) : null}
             onSave={editingGoal ? updateGoal : createGoal}
             onCancel={() => { setShowGoalForm(false); setEditingGoal(null); setParentGoalId(null); }}
           />
         )}
        </div>
      </div>
    </div>
  );
};

export default GoalTracker;